import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { shipment, shipmentSettingsFunc } from '../../../../models/entities/shipment/shipment';
import { businessPartner } from '../../../../models/entities/businessPartner/businessPartner';
import { PagesRoutes } from '../../../../routing/PagesRoutes';
import { allShipmentsPageSelectors } from '../../../../state/ducks/allShipmentsPage/selectors';
import { localizationSelectors } from '../../../../state/ducks/localization/selectors';
import { shipmentsSelectors } from '../../../../state/ducks/shipments/selectors';
import { singleShipmentSelectors } from '../../../../state/ducks/singleShipment/selectors';
import { userSelectors } from '../../../../state/ducks/user/selectors';
import { RootState } from '../../../../state/store/store';
import { userSettings } from '../../../../models/entities/user';
import Grid from '../../../shared/Grid/Grid';
import HouseShipmentsTable from './HouseShipmentsTable';
import ConsolidationDocumentsTable from './ConsolidationDocumentsTable';
import ConsolidationEventsTable from './ConsolidationEventsTable';
import { filterColsByUserSettings } from '../../../shared/Grid/util';
import { ColumnType } from '../../../shared/Grid/types/Column';
import BackButton from '../../../shared/SmallComponents/BackButton';
import Loader from '../../../shared/SmallComponents/Loader';
import TabsContainer from '../../../shared/Tabs/TabsContainer';
import Tab from '../../../shared/Tabs/Tab';
import StyledLink from '../../../shared/SmallComponents/StyledLink';
import History from '../../commonSubEntities/history/HistoryTab';
import { styled } from '../../../shared/Theme/theme';
import ConsolidationDetailsHeader from './ConsolidationDetailsHeader';
import { businessPartnersSelectors } from '../../../../state/ducks/businessPartner/selectors';
import {
    aggregateDiscussions,
    aggregateDocumentCountsByType,
    aggregateDocuments,
    aggregateEvents,
    aggregateHistory,
    buildCommodityBreakdown,
    buildConsolidationSummary,
    buildHousePrefillInitialValues,
    ConsolidationDiscussionRow,
    pickMasterShipmentForEdit,
    prioritizeShipmentLinkColumn
} from './consolidationUtils';

type Props = {
    shipments: shipment[];
    localization: any;
    gridColumns: Array<ColumnType<shipment>>;
    shipmentsGridSettings?: userSettings;
    lastVisitShipmentId?: string;
    showGridColumnsLoader?: boolean;
    showShipmentsLoader?: boolean;
    fetchShipments: () => void;
    fetchGridColumns: () => void;
    settings: shipmentSettingsFunc;
    getBusinessPartnerById: (id: string) => businessPartner | undefined;
};

const ConsolidationDetailsPageConnected = ({
    shipments,
    localization,
    gridColumns,
    shipmentsGridSettings,
    lastVisitShipmentId,
    showGridColumnsLoader,
    showShipmentsLoader,
    fetchShipments,
    fetchGridColumns,
    settings,
    getBusinessPartnerById
}: Props) => {
    const { master: masterParam = '' } = useParams<{ master: string }>();
    const master = decodeURIComponent(masterParam);
    const [activeTab, setActiveTab] = useState('Overview');

    useEffect(() => {
        fetchShipments();
        fetchGridColumns();
    }, [fetchShipments, fetchGridColumns]);

    const groupedShipments = useMemo(
        () => shipments.filter((currentShipment) => currentShipment.master === master),
        [shipments, master]
    );

    const summary = useMemo(() => buildConsolidationSummary(groupedShipments, master), [groupedShipments, master]);
    const commodityBreakdown = useMemo(() => buildCommodityBreakdown(groupedShipments), [groupedShipments]);
    const allDocuments = useMemo(() => aggregateDocuments(groupedShipments), [groupedShipments]);
    const documentCounts = useMemo(() => aggregateDocumentCountsByType(groupedShipments), [groupedShipments]);
    const allEvents = useMemo(() => aggregateEvents(groupedShipments), [groupedShipments]);
    const allHistory = useMemo(() => aggregateHistory(groupedShipments), [groupedShipments]);
    const allDiscussions = useMemo(() => aggregateDiscussions(groupedShipments), [groupedShipments]);

    const shipmentSettings = settings(groupedShipments[0]);
    const stateSetting = shipmentSettings?.state?.find((item) => item.code === summary.status);
    const stateLabel = stateSetting?.name || summary.status;
    const shipmentsGridColumns = useMemo(
        () => prioritizeShipmentLinkColumn(filterColsByUserSettings(gridColumns, shipmentsGridSettings)),
        [gridColumns, shipmentsGridSettings]
    );

    const formatDate = (date?: Date) => (date ? moment(date).format('DD/MM/YYYY') : '-');
    const formatDateTime = (date?: Date) => (date ? moment(date).format('DD/MM/YYYY HH:mm') : '-');

    if (showShipmentsLoader && !groupedShipments.length) {
        return (
            <PageWrapper>
                <LoadingWrap>
                    <BackButton toPath={PagesRoutes.ShipmentsConsolidations} text="Back to Consolidations" />
                    <Loader />
                </LoadingWrap>
            </PageWrapper>
        );
    }

    if (!groupedShipments.length) {
        return (
            <PageWrapper>
                <NotFoundHeader>
                    <BackButton toPath={PagesRoutes.ShipmentsConsolidations} text="Back to Consolidations" />
                    <NotFoundTitle>Consolidation not found</NotFoundTitle>
                </NotFoundHeader>
            </PageWrapper>
        );
    }

    const masterShipment = pickMasterShipmentForEdit(groupedShipments);
    const firstShipmentId = masterShipment.id;
    const handlingAgentName = summary.handlingAgentId
        ? getBusinessPartnerById(summary.handlingAgentId)?.name || summary.handlingAgentId
        : '—';
    const housePrefillInitialValues = useMemo(() => {
        const referenceShipment =
            groupedShipments.find((currentShipment) => currentShipment.house) || groupedShipments[0];
        return buildHousePrefillInitialValues(summary, master, referenceShipment);
    }, [summary, master, groupedShipments]);

    return (
        <PageWrapper>
            <ConsolidationDetailsHeader
                master={master}
                firstShipmentId={firstShipmentId}
                housePrefillInitialValues={housePrefillInitialValues}
                summary={{
                    ...summary,
                    stateLabel,
                    stateColor: stateSetting?.color,
                    handlingAgentName
                }}
            />

            <TabsSection>
                <TabsContainer showTabBarBorder={false}>
                    <Tab title="Overview" isActive={activeTab === 'Overview'} onClick={() => setActiveTab('Overview')}>
                        <OverviewLayout>
                            <MainColumn>
                                <Card>
                                    <CardHeader>
                                        <SectionTitle>Consolidation Details</SectionTitle>
                                    </CardHeader>
                                    <DetailsGrid>
                                        <DetailItem>
                                            <Label>Type</Label>
                                            <Value>{summary.typeLabel}</Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>Master AWB</Label>
                                            <ValueLink>{master}</ValueLink>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>Carrier</Label>
                                            <Value>{summary.carrier}</Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>Flight / Voyage</Label>
                                            <Value>{summary.flightOrVoyage}</Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>Route</Label>
                                            <Value>{summary.route}</Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>ETD</Label>
                                            <Value>{formatDateTime(summary.etd)}</Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>ETA</Label>
                                            <Value>{formatDateTime(summary.eta)}</Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>Created By</Label>
                                            <Value>{summary.createdBy}</Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>Created On</Label>
                                            <Value>{formatDateTime(summary.createdAt)}</Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>Status</Label>
                                            <Value>{stateLabel}</Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>House Shipments</Label>
                                            <Value>{summary.shipmentCount}</Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>Total Weight</Label>
                                            <Value>
                                                {summary.totalWeight} {summary.weightUnit}
                                            </Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>Total Volume</Label>
                                            <Value>
                                                {summary.totalVolume} {summary.volumeUnit}
                                            </Value>
                                        </DetailItem>
                                        <DetailItem>
                                            <Label>Commodity</Label>
                                            <Value>{summary.commodity}</Value>
                                        </DetailItem>
                                    </DetailsGrid>
                                </Card>

                                <HouseShipmentsCard>
                                    <HouseCardHeader>
                                        <SectionTitle>House Shipments ({summary.shipmentCount})</SectionTitle>
                                    </HouseCardHeader>
                                    <HouseShipmentsTable
                                        shipments={groupedShipments}
                                        settings={settings}
                                        totalPieces={summary.totalPieces}
                                        totalWeight={summary.totalWeight}
                                        totalVolume={summary.totalVolume}
                                    />
                                    <HouseCardFooter>
                                        <ViewAllLink type="button" onClick={() => setActiveTab('Shipments')}>
                                            View all shipments in list
                                        </ViewAllLink>
                                    </HouseCardFooter>
                                </HouseShipmentsCard>
                            </MainColumn>

                            <SideColumn>
                                <WidgetCard>
                                    <SectionTitle>Consolidation Summary</SectionTitle>
                                    {commodityBreakdown.map((item) => (
                                        <BreakdownRow key={item.name}>
                                            <BreakdownLabel>{item.name}</BreakdownLabel>
                                            <BreakdownBar>
                                                <BreakdownFill $width={item.percent} />
                                            </BreakdownBar>
                                            <BreakdownPercent>{item.percent}%</BreakdownPercent>
                                        </BreakdownRow>
                                    ))}
                                    <WidgetTotals>
                                        <div>
                                            <Label>Total Weight</Label>
                                            <Value>
                                                {summary.totalWeight} {summary.weightUnit}
                                            </Value>
                                        </div>
                                        <div>
                                            <Label>Total Volume</Label>
                                            <Value>
                                                {summary.totalVolume} {summary.volumeUnit}
                                            </Value>
                                        </div>
                                    </WidgetTotals>
                                </WidgetCard>

                                <WidgetCard>
                                    <SectionTitle>Documents</SectionTitle>
                                    <DocCountRow>
                                        <span>Master AWB</span>
                                        <strong>1</strong>
                                    </DocCountRow>
                                    <DocCountRow>
                                        <span>House AWBs</span>
                                        <strong>{documentCounts.houseAwbs}</strong>
                                    </DocCountRow>
                                    {documentCounts.byType.slice(0, 4).map((item) => (
                                        <DocCountRow key={item.type}>
                                            <span>{item.type}</span>
                                            <strong>{item.count}</strong>
                                        </DocCountRow>
                                    ))}
                                    <ViewAllLink type="button" onClick={() => setActiveTab('Documents')}>
                                        View all documents →
                                    </ViewAllLink>
                                </WidgetCard>
                            </SideColumn>
                        </OverviewLayout>
                    </Tab>

                    <Tab
                        title={`Shipments (${summary.shipmentCount})`}
                        isActive={activeTab === 'Shipments'}
                        onClick={() => setActiveTab('Shipments')}
                    >
                        <ShipmentsGridWrapper>
                            <Grid<shipment>
                                columns={shipmentsGridColumns}
                                data={groupedShipments}
                                localization={localization.grid}
                                settings={settings}
                                emptyStateText={localization.grid?.emptyState || 'No shipments found for this consolidation.'}
                                showLoader={showGridColumnsLoader}
                                idKey="id"
                                userSettingKey="ShipmentGrid"
                                lastVisitId={lastVisitShipmentId}
                                menuType="shipmentMenu"
                                menuBindedFields={['CargoZoneNumber', 'companyId']}
                                onRowMoreOptionsClick={() => undefined}
                            />
                        </ShipmentsGridWrapper>
                    </Tab>

                    <Tab title="Documents" isActive={activeTab === 'Documents'} onClick={() => setActiveTab('Documents')}>
                        <TabContentPad>
                            <SectionTitle>Documents ({allDocuments.length})</SectionTitle>
                            <ConsolidationDocumentsTable documents={allDocuments} settings={shipmentSettings} />
                        </TabContentPad>
                    </Tab>

                    <Tab title="Events" isActive={activeTab === 'Events'} onClick={() => setActiveTab('Events')}>
                        <TabContentPad>
                            <SectionTitle>Events ({allEvents.length})</SectionTitle>
                            <ConsolidationEventsTable
                                events={allEvents}
                                settings={shipmentSettings}
                                localization={localization}
                            />
                        </TabContentPad>
                    </Tab>

                    <Tab title="Notes" isActive={activeTab === 'Notes'} onClick={() => setActiveTab('Notes')}>
                        <TabContentPad>
                            <SectionTitle>Notes ({allDiscussions.length})</SectionTitle>
                            {allDiscussions.length === 0 ? (
                                <PlaceholderText>No notes on shipments in this consolidation.</PlaceholderText>
                            ) : (
                                <NotesList>
                                    {allDiscussions.map((note) => (
                                        <NoteItem key={note.id}>
                                            <NoteHeader>
                                                <StyledLink to={`${PagesRoutes.Shipments}/${note.shipmentId}`}>
                                                    {note.shipmentNumber}
                                                </StyledLink>
                                                <span>{note.userName}</span>
                                                <span>{formatDateTime(note.createdDate)}</span>
                                            </NoteHeader>
                                            <NoteBody dangerouslySetInnerHTML={{ __html: note.message }} />
                                        </NoteItem>
                                    ))}
                                </NotesList>
                            )}
                        </TabContentPad>
                    </Tab>

                    <Tab title="History" isActive={activeTab === 'History'} onClick={() => setActiveTab('History')}>
                        <TabContentPad>
                            {allHistory.length ? (
                                <History history={allHistory} localization={localization} />
                            ) : (
                                <PlaceholderText>No history recorded for shipments in this consolidation.</PlaceholderText>
                            )}
                        </TabContentPad>
                    </Tab>
                </TabsContainer>
            </TabsSection>
        </PageWrapper>
    );
};

const PageWrapper = styled.div`
    height: 100%;
    display: flex;
    flex-direction: column;
    overflow: overlay;
    background: #f3f3f3;
`;

const LoadingWrap = styled.div`
    padding: 24px 18px 16px 48px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    align-items: flex-start;
`;

const NotFoundHeader = styled.div`
    padding: 24px 18px 16px 48px;
`;

const NotFoundTitle = styled.h1`
    margin: 12px 0 0;
    font-size: 22px;
    font-weight: bold;
    color: ${(props) => props.theme.colors.pageTitle};
`;

const TabsSection = styled.div`
    width: 100%;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    background: #f3f3f3;

    #tabs-container {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
    }

    /* Tab labels sit on white chrome — no full-width rule under metrics */
    #tabs-container > div:first-child {
        background: #fff;
        padding: 0 18px 0 48px;
        border-bottom: none;
    }

    #tabs-container > div:last-child {
        flex: 1;
        min-height: 0;
        overflow: overlay;
    }
`;

const OverviewLayout = styled.div`
    display: flex;
    gap: 20px;
    padding: 20px 18px 24px 48px;
    align-items: flex-start;

    @media screen and (max-width: 1100px) {
        flex-direction: column;
    }
`;

const MainColumn = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const SideColumn = styled.div`
    width: 300px;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;

    @media screen and (max-width: 1100px) {
        width: 100%;
    }
`;

const Card = styled.div`
    background: #fff;
    border: 1px solid ${(props) => props.theme.colors.grayDefaultBorder};
    border-radius: 8px;
    padding: 16px 18px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.06);
`;

const HouseShipmentsCard = styled(Card)`
    padding: 0;
`;

const HouseCardHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 18px 8px;
`;

const HouseCardFooter = styled.div`
    padding: 10px 18px 14px;
    border-top: none;
`;

const WidgetCard = styled(Card)`
    padding: 16px;
`;

const CardHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 14px;
`;

const SectionTitle = styled.h3`
    margin: 0;
    color: ${(props) => props.theme.colors.secondary};
    font-size: 16px;
`;

const DetailsGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 14px 20px;

    @media screen and (max-width: 900px) {
        grid-template-columns: repeat(2, 1fr);
    }
    @media screen and (max-width: 600px) {
        grid-template-columns: 1fr;
    }
`;

const DetailItem = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`;

const Label = styled.div`
    font-size: 12px;
    color: ${(props) => props.theme.colors.lightGray};
    text-transform: uppercase;
`;

const Value = styled.div`
    color: ${(props) => props.theme.colors.secondary};
    font-size: ${(props) => props.theme.fontSizes.gridCell};
    font-weight: 600;
`;

const ValueLink = styled(Value)`
    color: ${(props) => props.theme.colors.link};
`;

const ViewAllLink = styled.button`
    border: none;
    background: none;
    color: ${(props) => props.theme.colors.link};
    cursor: pointer;
    font-size: ${(props) => props.theme.fontSizes.gridCell};
    padding: 0;
`;

const BreakdownRow = styled.div`
    display: grid;
    grid-template-columns: 80px 1fr 36px;
    gap: 8px;
    align-items: center;
    margin-bottom: 10px;
`;

const BreakdownLabel = styled.span`
    font-size: 12px;
    color: ${(props) => props.theme.colors.secondary};
`;

const BreakdownBar = styled.div`
    height: 8px;
    background: #e8eef3;
    border-radius: 4px;
    overflow: hidden;
`;

const BreakdownFill = styled.div<{ $width: number }>`
    height: 100%;
    width: ${(props) => props.$width}%;
    background: ${(props) => props.theme.colors.primaryBlue};
    border-radius: 4px;
`;

const BreakdownPercent = styled.span`
    font-size: 12px;
    color: ${(props) => props.theme.colors.lightGray};
    text-align: right;
`;

const WidgetTotals = styled.div`
    display: flex;
    justify-content: space-between;
    margin-top: 16px;
    padding-top: 12px;
    border-top: 1px solid ${(props) => props.theme.colors.grayDefaultBorder};
`;

const DocCountRow = styled.div`
    display: flex;
    justify-content: space-between;
    padding: 6px 0;
    font-size: ${(props) => props.theme.fontSizes.gridCell};
    color: ${(props) => props.theme.colors.secondary};
`;

/** Same grid container as All Shipments page (ShipmentsPage GridWrapper). */
const ShipmentsGridWrapper = styled.div`
    flex: 1;
    max-width: 100%;
    width: 100%;
    box-sizing: border-box;
    overflow: overlay;
    height: 100%;
    min-height: calc(100vh - 220px);
    z-index: 1;
    background: #fff;
`;

const TabContentPad = styled.div`
    padding: 20px 18px 24px 48px;
`;

const PlaceholderText = styled.div`
    padding: 12px 0;
    color: ${(props) => props.theme.colors.lightGray};
`;

const NotesList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const NoteItem = styled.div`
    border: 1px solid ${(props) => props.theme.colors.grayDefaultBorder};
    border-radius: 6px;
    padding: 12px;
    background: #fff;
`;

const NoteHeader = styled.div`
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 8px;
    font-size: 12px;
    color: ${(props) => props.theme.colors.lightGray};
`;

const NoteBody = styled.div`
    font-size: ${(props) => props.theme.fontSizes.gridCell};
    color: ${(props) => props.theme.colors.secondary};
`;

const mapStateToProps = (state: RootState) => ({
    shipments: shipmentsSelectors.shipmentsData(state),
    localization: localizationSelectors.singleShipmentPage(state),
    settings: shipmentsSelectors.settingsByLanguage(state),
    gridColumns: allShipmentsPageSelectors.gridColumns(state),
    shipmentsGridSettings: userSelectors.shipmentsGridSettings(state),
    lastVisitShipmentId: singleShipmentSelectors.currentShipmentId(state) || undefined,
    showGridColumnsLoader: state.loading.effects.allShipmentsPage.fetchGridColumns,
    showShipmentsLoader: Boolean(state.loading.effects.shipments.fetchShipments),
    getBusinessPartnerById: (id: string) => businessPartnersSelectors.getById(state, id)
});

const mapDispatchToProps = (dispatch: any) => ({
    fetchShipments: () => dispatch.shipments.fetchShipments(),
    fetchGridColumns: () => dispatch.allShipmentsPage.fetchGridColumns()
});

export default connect(mapStateToProps, mapDispatchToProps)(ConsolidationDetailsPageConnected);
